import type { ReactNode } from 'react';
import type { ByPersonProps, PersonRow, PersonCard } from './types';

/**
 * THE PORTFOLIO, TURNED THE OTHER WAY — by the person, not the project. The
 * projects lens (ProjectsLens) answers where each project is; this lens answers
 * the SECOND question a weekly meeting asks — who is accountable, and what are
 * they doing — over the very same tenant cards. It is a leaf: every figure is
 * already derived and pre-sorted in `src/accountability.ts`, so this only lays
 * it out.
 *
 * The design language is the projects lens's, on purpose, so the two read as one
 * product: the same `.pf-head`/`.pf-sub` title block, the same health pill words
 * and classes, the same `.chip`. A person is richer than a table row, so each is
 * a card in the portfolio's own card language, not a dense row. The unowned work
 * is surfaced FIRST, in the app's attention tint (never at-risk red), because an
 * unowned card is an accountability gap, not a blank to hide.
 */

import { MONTHS } from '../../when';
import InfoTip from '../InfoTip';
import type { Overload, PersonCapacity, CapacityWeek, CapacityCard } from '../../accountability';
import type { IssueSize } from '../../types';
import { isWorkingDay, closesBetween, type WorkCalendar, DEFAULT_CALENDAR } from '../../work-calendar';

/** The health pill word/class — the roll-up's, verbatim, so a person and their
 *  projects never disagree about what "on track" looks like. */
const HEALTH_WORD: Record<'green' | 'amber' | 'red', string> = { green: 'On track', amber: 'At risk', red: 'Off track' };
const HEALTH_CLS: Record<'green' | 'amber' | 'red', string> = { green: 'g', amber: 'a', red: 'r' };

/** THE OVERLOAD FLAG (#436) — "are they carrying too much at once?", distinct
 *  from the health pill ("is their work stuck?"). A stacked-bars glyph (never a
 *  dot) and its own tone, so it never reads as a second health pill. Shown only
 *  when it has something to say. */
const OVERLOAD_WORD: Record<Exclude<Overload, 'none'>, string> = { busy: 'Busy', overloaded: 'Overloaded' };
/** Three rising bars — a "how much is on them" mark, not a status dot. */
function LoadBars() {
  return (
    <svg className="pfp-ol-bars" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="1" y="8" width="3" height="5" rx="1" />
      <rect x="5.5" y="5" width="3" height="8" rx="1" />
      <rect x="10" y="2" width="3" height="11" rx="1" />
    </svg>
  );
}
function OverloadFlag({ level }: { level: Overload }) {
  if (level === 'none') return null;
  return (
    <span className={`pfp-overload ${level}`}>
      <LoadBars />
      {OVERLOAD_WORD[level]}
    </span>
  );
}

/** The capacity meter — the in-flight count drawn against the cap, so the flag
 *  explains itself: `cap` cells fill as work piles up, and anything over the cap
 *  spills into red "over" cells. The line names the count and, when over, by how
 *  much. */
function CapacityMeter({ inFlight, cap, level }: { inFlight: number; cap: number; level: Overload }) {
  const filled = Math.min(inFlight, cap);
  const over = Math.max(0, inFlight - cap);
  const cells = [];
  for (let i = 0; i < cap; i++) cells.push(<span key={`c${i}`} className={`pfp-cap-cell${i < filled ? ` ${level === 'none' ? 'fill' : level}` : ''}`} />);
  for (let i = 0; i < over; i++) cells.push(<span key={`o${i}`} className="pfp-cap-cell over" />);
  return (
    <span className="pfp-cap">
      <span className="pfp-cap-cells">{cells}</span>
      {over > 0 ? (
        <span className="pfp-cap-l"><b>{inFlight}</b> in flight · <b className="pfp-cap-over">{over} over</b> the {cap} cap</span>
      ) : (
        <span className="pfp-cap-l"><b>{inFlight}</b> of {cap} in flight</span>
      )}
    </span>
  );
}

/** The snapshot date, formatted as the roll-up formats it: "18 Sep 2026". */
function snapLabel(at?: number): string {
  const d = new Date(at ?? Date.now());
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** A `YYYY-MM-DD` → "20 Sep", for the due date on a line. */
function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]}`;
}

/** The screen title block — the same treatment the other portfolio lenses use. */
function Head({ sub }: { sub: string }) {
  return (
    <div className="pf-head">
      <div className="pf-head-l">
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>Who&apos;s accountable — and what they&apos;re doing</div>
        <div className="pf-sub">{sub}</div>
      </div>
    </div>
  );
}

/** One clickable card line — its reference, title, due date, and the children
 *  that follow (a project chip, or a blocker + age). The whole line opens the
 *  card's detail IN PLACE (the lean read-only panel), so a card is more than a
 *  title — you see its due date on the line and everything else on click,
 *  without leaving the portfolio. */
function CardLine({ card, onOpenCard, children }: { card: PersonCard; onOpenCard: (projectId: string, cardId: string) => void; children: React.ReactNode }) {
  const overdue = card.overdueDays > 0;
  return (
    <button type="button" className="pfp-item" onClick={() => onOpenCard(card.projectId, card.id)}>
      <span className="pfp-ref">{card.ref}</span>
      <span className="pfp-title">{card.title}</span>
      {card.dueDay && (
        <span className={`pfp-due${overdue ? ' over' : ''}`}>
          {overdue ? `${fmtDay(card.dueDay)} · ${card.overdueDays}d over` : `due ${fmtDay(card.dueDay)}`}
        </span>
      )}
      {children}
    </button>
  );
}

/** The unowned work, set apart at the top in the attention tint — the gap the
 *  view surfaces on purpose. Names the open cards it can (in-motion and blocked
 *  carry a reference), and points at the boards the rest live on so every one is
 *  reachable, even the backlog cards a row does not enumerate. */
function UnassignedCallout({ row, onOpenBoard, onOpenCard }: { row: PersonRow; onOpenBoard: (id: string) => void; onOpenCard: (projectId: string, cardId: string) => void }) {
  // Every unowned OPEN card, named so each can be opened and given an owner — a
  // blocked one keeps its blocker + age, the rest show the project they sit on.
  const blockedRefs = new Set(row.waiting.map((c) => c.ref));
  const shown = row.openCards.slice(0, 8);
  const gap = row.open;
  const more = Math.max(0, gap - shown.length);

  return (
    <div className="pfp-callout">
      <div className="pfp-callout-h">{gap} {gap === 1 ? 'card has' : 'cards have'} no owner</div>
      <div className="pfp-callout-sub">Unowned work is an accountability gap — open a card to give it an owner.</div>
      {shown.length > 0 && (
        <div className="pfp-lines">
          {shown.map((c) => (
            <CardLine key={c.ref} card={c} onOpenCard={onOpenCard}>
              {blockedRefs.has(c.ref) && c.blockerLabel ? (
                <>
                  <span className="pfp-blocker">{c.blockerLabel}</span>
                  {c.blockerDays != null && <span className={`pfp-age${c.blockerDays > 14 ? ' old' : ''}`}>{c.blockerDays}d</span>}
                </>
              ) : (
                <span className="chip pfp-proj">{c.projectName}</span>
              )}
            </CardLine>
          ))}
        </div>
      )}
      {more > 0 && (
        <div className="pfp-callout-more">
          <span>{shown.length > 0 ? `+${more} more without an owner` : `${more} without an owner`} · on</span>
          {row.boards.map((b) => (
            <button key={b.id} type="button" className="chip pfp-proj" onClick={() => onOpenBoard(b.id)}>{b.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The size → letter the "due in this window" line shows (the effort's source). */
const SIZE_LETTER: Record<IssueSize, string> = { quick: 'S', medium: 'M', long: 'L' };

/** A whole number of days as "7 days" / "1 day" — the size-weighted follow-up window is in
 *  whole working days and always lands whole, so no decimals are shown. */
const daysLabel = (n: number): string => `${Math.round(n)} day${Math.round(n) === 1 ? '' : 's'}`;

/** `YYYY-MM-DD` + n days, as `YYYY-MM-DD` (UTC) — for walking to a window's last day. */
function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
/** A `YYYY-MM-DD` as UTC-midnight ms — for the holiday lookup over a week. */
function isoMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}
/** The last working day on or before `iso` (walk back over weekend/holidays), so the
 *  summary reads "N working days to <the last day they actually work>". */
function lastWorkingDay(iso: string, cal: WorkCalendar): string {
  let d = iso;
  for (let i = 0; i < 10 && !isWorkingDay(d, cal); i++) d = addDaysISO(d, -1);
  return d;
}

/** A small calendar glyph — the mark a week carries a workspace holiday. A plain
 *  outline, never an emoji (R-no-emoji), in the app's own line colour. */
function CalGlyph() {
  return (
    <svg className="pfp-cal" width="13" height="13" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" fill="none" strokeWidth="1.2" />
      <path d="M1.5 5.5 H12.5" strokeWidth="1.2" />
      <path d="M4.5 1.5 V3.5 M9.5 1.5 V3.5" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/** One week's bar: the size-weighted committed load drawn against the working days
 *  the calendar leaves that week. A dashed line marks available; load below it is in
 *  capacity (teal), anything above spills over (red). A holiday week carries the glyph. */
function WeekBar({ w, perMax, cal }: { w: CapacityWeek; perMax: number; cal: WorkCalendar }) {
  const H = 72;
  // A little headroom above the busiest week, so the availability line never sits flush
  // at the track's top edge where its "Nd" label (drawn just above the line) would clip
  // under the track's overflow:hidden — the common comfortable-week case.
  const denom = Math.max(1, perMax) * 1.15;
  const inCap = Math.min(w.committed, w.available);
  const over = Math.max(0, w.committed - w.available);
  const tealPx = (inCap / denom) * H;
  const overPx = (over / denom) * H;
  const linePx = (w.available / denom) * H;
  const hol = closesBetween(isoMs(w.weekStart), isoMs(w.weekEnd), cal).length > 0;
  return (
    <div className="pfp-bar">
      <div className={`pfp-bar-lab${w.over ? ' over' : ''}`}>{Math.round(w.committed)} / {w.available}d</div>
      <div className="pfp-bar-track" style={{ height: H }}>
        {hol && <span className="pfp-bar-hol"><CalGlyph /></span>}
        <div className="pfp-bar-fill" style={{ height: `${tealPx}px` }} />
        {overPx > 0 && <div className="pfp-bar-over" style={{ bottom: `${linePx}px`, height: `${overPx}px` }} />}
        <div className="pfp-bar-line" style={{ bottom: `${linePx}px` }}><span>{w.available}d</span></div>
      </div>
      <div className="pfp-bar-wk">wk of<br />{fmtDay(w.weekStart)}</div>
    </div>
  );
}

/** THE FORWARD LOAD (0216) — "how loaded, and which week they tip over". A one-line
 *  verdict and summary, a bar per week against the working days available, and the
 *  cards due in the window. Only shown when the person has due-dated work ahead. */
function CapacityCardLines({ cards, onOpenCard, dateOf }: {
  cards: CapacityCard[];
  onOpenCard: (projectId: string, cardId: string) => void;
  dateOf: (c: CapacityCard) => ReactNode;
}) {
  return (
    <div className="pfp-lines">
      {cards.map((c) => (
        <button type="button" className="pfp-item" key={c.id} onClick={() => onOpenCard(c.projectId, c.id)}>
          <span className="pfp-ref">{c.ref}</span>
          <span className="pfp-title">{c.title}</span>
          {dateOf(c)}
          {c.projectName && <span className="chip pfp-proj">{c.projectName}</span>}
        </button>
      ))}
    </div>
  );
}

function CapacityBlock({ cap, cal, onOpenCard }: { cap: PersonCapacity; cal: WorkCalendar; onOpenCard: (projectId: string, cardId: string) => void }) {
  // The load states, kept apart: being over the whole window's capacity is a different
  // fact from a single week spiking, and both differ from a backlog of overdue work or
  // work with no date to place it. The badge names whichever is most pressing, and a
  // person with hidden work (overdue/undated) never reads "Comfortable" (monitoring-
  // witness, 1 Nov).
  const hasForward = cap.committedTotal > 0;
  const hasOverdue = cap.overdueEffort > 0;
  const hasUndated = cap.undatedCards.length > 0;
  const overAll = cap.loadPct > 100;
  const weekOver = !overAll && cap.firstOverWeek != null;
  const badge = overAll ? { cls: 'over', word: 'Over-allocated' }
    : weekOver ? { cls: 'warn', word: 'A week over' }
    : hasOverdue ? { cls: 'warn', word: 'Behind' }
    : (hasUndated && !hasForward) ? { cls: 'muted', word: 'Undated work' }
    : { cls: 'ok', word: 'Comfortable' };
  const clear = !overAll && !weekOver && !hasOverdue && !hasUndated;
  const endDay = lastWorkingDay(cap.weeks[cap.weeks.length - 1].weekEnd, cal);
  const perMax = Math.max(1, ...cap.weeks.map((w) => Math.max(w.committed, w.available)));
  const anyOver = cap.weeks.some((w) => w.over);
  // The first holiday falling on a working day in the window, for the legend line.
  const holiday = (() => {
    for (const w of cap.weeks) {
      const c = closesBetween(isoMs(w.weekStart), isoMs(w.weekEnd), cal);
      if (c.length) return c[0];
    }
    return null;
  })();
  // The forward cards due in the window, soonest first — the work behind the bars.
  const due = cap.weeks.flatMap((w) => w.cards).sort((a, b) => (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : 0));

  return (
    <div className="pfp-cap2">
      <div className="pfp-cap2-top">
        <span className="pfp-cap2-h">
          Load ahead — next 3 weeks
          <InfoTip label="Load ahead">
            How much attention this person's dated cards are likely to need over the next three
            weeks, against the working days they have. Each card counts for how long it can sit
            before someone would chase it — a larger card can wait longer, so it weighs more. It is
            a planning estimate from each card's size, <b>not</b> time anyone logged or an effort
            figure they typed.
            <br />
            <br />
            Example: a card with no size set is treated as a medium — about a working week.
          </InfoTip>
        </span>
        <span className={`pfp-alloc ${badge.cls}`}><LoadBars />{badge.word}</span>
        <span className="pfp-cap2-sum">
          <b>{daysLabel(cap.committedTotal)}</b> committed · <b>{cap.availableTotal}</b> working days to {fmtDay(endDay)} · <b>{cap.loadPct}%</b>
          {weekOver && <> · first tips over the week of <b>{fmtDay(cap.firstOverWeek!)}</b></>}
          {hasOverdue && <> · <b className="pfp-sum-over">{daysLabel(cap.overdueEffort)} overdue</b></>}
          {hasUndated && <> · <b className="pfp-sum-undated">{cap.undatedCards.length} undated, not placed</b></>}
          {clear && <> · room to take on more</>}
        </span>
      </div>

      {hasForward && (
        <div className="pfp-cap2-chart">
          <div className="pfp-bars">
            {cap.weeks.map((w) => <WeekBar key={w.weekStart} w={w} perMax={perMax} cal={cal} />)}
          </div>
          <div className="pfp-cap2-legend">
            <span className="lg"><span className="sw committed" />committed load (size-weighted)</span>
            {anyOver && <span className="lg"><span className="sw over" />over capacity</span>}
            <span className="lg"><span className="sw avail" />working days available</span>
            {holiday && (
              <span className="lg lg-holiday">
                <CalGlyph />
                <span className="lg-holiday-lines">
                  <span>holiday that week</span>
                  <span className="lg-holiday-when">{fmtDay(holiday.date)} · {holiday.name}</span>
                </span>
              </span>
            )}
          </div>
        </div>
      )}

      {due.length > 0 && (
        <div className="pfp-sec">
          <div className="pfp-sec-h">Due in this window</div>
          <CapacityCardLines cards={due} onOpenCard={onOpenCard}
            dateOf={(c) => <span className="pfp-due">due {fmtDay(c.dueDay)}{c.size ? ` · ${SIZE_LETTER[c.size]}` : ''}</span>} />
        </div>
      )}

      {cap.overdueCards.length > 0 && (
        <div className="pfp-sec">
          <div className="pfp-sec-h">Overdue — already past due</div>
          <CapacityCardLines cards={cap.overdueCards} onOpenCard={onOpenCard}
            dateOf={(c) => <span className="pfp-due over">due {fmtDay(c.dueDay)}{c.size ? ` · ${SIZE_LETTER[c.size]}` : ''}</span>} />
        </div>
      )}

      {cap.undatedCards.length > 0 && (
        <div className="pfp-sec">
          <div className="pfp-sec-h">Undated — not placed on the load</div>
          <CapacityCardLines cards={cap.undatedCards} onOpenCard={onOpenCard}
            dateOf={(c) => <span className="pfp-due undated">no due date{c.size ? ` · ${SIZE_LETTER[c.size]}` : ''}</span>} />
        </div>
      )}
    </div>
  );
}

/** One person — a card in the portfolio's card language. Header (name · health ·
 *  the inferred-attribution note), a load summary, then the two lists that
 *  answer the questions: what they're doing now, and what they're waiting on. */
function Person({ row, cal, onOpenCard }: { row: PersonRow; cal: WorkCalendar; onOpenCard: (projectId: string, cardId: string) => void }) {
  const active = row.active.slice(0, 4);
  const activeMore = row.active.length - active.length;
  const waiting = row.waiting.slice(0, 3);
  const waitingMore = row.waiting.length - waiting.length;
  const boards = row.boards.length;

  return (
    <div className="pfp-card">
      <div className="pfp-top">
        <span className="pfp-name">{row.name}</span>
        <OverloadFlag level={row.overload} />
        <span className={`rag ${HEALTH_CLS[row.health]}`}><span className="dot" />{HEALTH_WORD[row.health]}</span>
        {row.attributed && <span className="pfp-note">some work inferred from a one-person board</span>}
      </div>

      <div className="pfp-load">
        <b>{row.open}</b> open · <b>{row.inProgress}</b> in progress · <b>{row.inReview}</b> in review · <b>{row.blocked}</b> blocked
        {boards > 0 && <span className="pfp-across"> · across {boards} project{boards === 1 ? '' : 's'}</span>}
        <CapacityMeter inFlight={row.inFlight} cap={row.overloadCap} level={row.overload} />
      </div>

      {/* HOW QUICKLY, not just how much — what they closed this week, and how long
          their oldest in-flight card has been sitting (the same aging language the
          project report uses). Counts + age only; never a velocity score. */}
      <div className="pfp-week">
        <span><b>{row.closedThisWeek}</b> closed this week</span>
        <span className="sep">·</span>
        <span>
          oldest in flight{' '}
          {row.oldestStuck ? (
            <span className={`age-chip${row.oldestStuck.aged ? ' old' : ''}`}>
              <span className="ref">{row.oldestStuck.ref}</span>{row.oldestStuck.aged ? `stalled ${row.oldestStuck.days} days` : `${row.oldestStuck.days} days, pressing`}
            </span>
          ) : (
            <b>none</b>
          )}
        </span>
      </div>

      {/* FORWARD LOAD — the predictive read: how loaded they are over the next three
          working weeks and which week they tip over. Only when there is dated work ahead. */}
      {row.capacity && <CapacityBlock cap={row.capacity} cal={cal} onOpenCard={onOpenCard} />}

      {active.length > 0 && (
        <div className="pfp-sec">
          <div className="pfp-sec-h">Doing now</div>
          <div className="pfp-lines">
            {active.map((c) => (
              <CardLine key={c.ref} card={c} onOpenCard={onOpenCard}>
                <span className="chip pfp-proj">{c.projectName}</span>
              </CardLine>
            ))}
            {activeMore > 0 && <div className="pfp-more">+{activeMore} more</div>}
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div className="pfp-sec">
          <div className="pfp-sec-h">Waiting on</div>
          <div className="pfp-lines">
            {waiting.map((c) => (
              <CardLine key={c.ref} card={c} onOpenCard={onOpenCard}>
                <span className="pfp-blocker">{c.blockerLabel}</span>
                {c.blockerDays != null && <span className={`pfp-age${c.blockerDays > 14 ? ' old' : ''}`}>{c.blockerDays}d</span>}
              </CardLine>
            ))}
            {waitingMore > 0 && <div className="pfp-more">+{waitingMore} more</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ByPerson({ acc, loading, at, calendar, onOpenBoard, onOpenCard }: ByPersonProps) {
  const cal = calendar ?? DEFAULT_CALENDAR;
  const sub = `By person · every card across the workspace, grouped by who's on it · snapshot of the week of ${snapLabel(at)}`;

  // First read, nothing yet — the head with a quiet line, as the roll-up does.
  if (loading && acc.people.length === 0) {
    return <Head sub="Loading…" />;
  }

  // Loaded, and there is genuinely nothing to show.
  if (acc.people.length === 0 && !acc.unassigned) {
    return (
      <>
        <Head sub={sub} />
        <div className="pfp-empty">No assigned work across the workspace yet.</div>
      </>
    );
  }

  return (
    <>
      <Head sub={sub} />
      {acc.unassigned && <UnassignedCallout row={acc.unassigned} onOpenBoard={onOpenBoard} onOpenCard={onOpenCard} />}
      <div className="pfp-list">
        {acc.people.map((p) => (
          <Person key={p.who} row={p} cal={cal} onOpenCard={onOpenCard} />
        ))}
      </div>
      <div className="mv-note" style={{ marginTop: 12 }}>
        A closed, started or pushed-back move is counted for whoever made it (from the activity log), not the card&rsquo;s current assignee — so &ldquo;closed this week&rdquo; credits the person who did it.
      </div>
    </>
  );
}
